/**
 * structure-summaries.ts — v4M7.1 pure helpers (no UI, no state mutation).
 *
 * Three helpers that turn a `StructuresData.byKind[kind].effects` array
 * into player-facing strings. Used by the v4M7 planet-panel UI work:
 *
 *   - `buildMenuSummaryFor(sd, kind)`
 *       Short per-L1 line for the Build button second line + the
 *       structure row "on completion" hint while still building.
 *       Returns the single dominant effect summary; multi-effect
 *       structures pick the headline (typically the first effect entry
 *       in the row).
 *
 *   - `structureContributionFor(sd, structure)`
 *       Current-level effect line for the structure row third line.
 *       Same formatter as the build menu but resolves against
 *       `structure.level` rather than L1.
 *
 *   - `effectLadderFor(sd, kind, currentLevel?)`
 *       Multi-line per-level breakdown for Build / Upgrade button
 *       tooltips. Always lists L1 through the structure's `maxLevel`.
 *       When `currentLevel` is provided, the matching line gets a
 *       `(currently)` marker so the upgrade tooltip doubles as a
 *       before/after comparison.
 *
 * Per the v4M0.4 lock, all formatting reads numeric values from the
 * `StructureRow.effects` array directly. No xlsx-side authoring of
 * summary strings — the displayed `+0.50 pop / tick at L1` always
 * matches what the sim actually does because both sides consume the
 * same `effect.perLevel[0]` value.
 *
 * Design lock: `docs/PLANET_UI.md §4`. Sub-checkpoint: v4M7.1.
 *
 * Strict-parity guarantee at this checkpoint: this module is not yet
 * imported by `detail-panel.ts`. v4M7.2 / .3 / .6 do the wire-up.
 */

import type {
  Structure,
  StructureEffect,
  StructureRow,
  StructuresData,
} from '../state/types/index';

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Short per-L1 effect line for the Build menu's second text row. Returns
 * `null` for structures whose row carries no effects (e.g. a Shipyard's
 * row would carry only a `shipyardClassGate` effect, which IS still
 * surfaced — `null` is the genuinely-no-effect case which v1 doesn't
 * currently have any of, but we model defensively).
 *
 * For multi-effect rows the dominant entry is used — see
 * `pickHeadlineEffect` below for the picking rule.
 */
export function buildMenuSummaryFor(
  sd: StructuresData,
  kind: string,
): string | null {
  const row = sd.byKind[kind];
  if (!row) return null;
  const effect = pickHeadlineEffect(row.effects);
  if (!effect) return null;
  return formatEffectAtLevel(effect, 1, /* withLevelSuffix */ true);
}

/**
 * Current-level effect line for the structure row third text line.
 * Mirrors `buildMenuSummaryFor` but reads `structure.level` instead of
 * pinning to L1. While the structure is still under initial construction
 * (`level === 0`), returns the "on completion (L1)" preview so the row
 * still tells the player what they're waiting for.
 */
export function structureContributionFor(
  sd: StructuresData,
  structure: Structure,
): string | null {
  const row = sd.byKind[structure.kind];
  if (!row) return null;
  const effect = pickHeadlineEffect(row.effects);
  if (!effect) return null;
  const effectiveLevel = Math.max(1, structure.level);
  const suffix = structure.level === 0 ? ' (on completion)' : '';
  return formatEffectAtLevel(effect, effectiveLevel, /* withLevelSuffix */ false) + suffix;
}

/**
 * Per-level effect breakdown for the Build / Upgrade tooltip. Returns
 * one line per level from 1 .. row.maxLevel; the line for `currentLevel`
 * (if provided + in-range) carries a trailing `(currently)` marker.
 *
 * Tooltip-side caller is responsible for joining with `\n`. Returning
 * an array keeps the formatting flexible (the Build button might join
 * differently than the Upgrade button if/when the formats diverge).
 */
export function effectLadderFor(
  sd: StructuresData,
  kind: string,
  currentLevel?: number,
): string[] {
  const row = sd.byKind[kind];
  if (!row) return [];
  const effect = pickHeadlineEffect(row.effects);
  if (!effect) return [];
  const out: string[] = [];
  for (let lvl = 1; lvl <= row.maxLevel; lvl++) {
    const base = `L${lvl}: ${formatEffectAtLevel(effect, lvl, /* withLevelSuffix */ false)}`;
    if (currentLevel !== undefined && currentLevel === lvl) {
      out.push(`${base} (currently)`);
    } else {
      out.push(base);
    }
  }
  return out;
}

// ---------------------------------------------------------------------------
// Internal — headline picker + per-effect formatter
// ---------------------------------------------------------------------------

/**
 * For a row with multiple effects, decide which one is the "headline" —
 * the single most player-meaningful summary. v1 picking rule, in order
 * of preference:
 *
 *   1. The first `perTickIncome` entry (treasury / research / fuel).
 *   2. The first `multiplier` entry (resource boosters).
 *   3. The first `capBonus` entry (storage caps).
 *   4. The first `shipyardClassGate` entry (build-class unlocks).
 *   5. The first `sensorRangeGalaxy` entry (LRSA).
 *   6. The first `weaponEmitter` entry (planet-side weapon-platform slot).
 *   7. The first `bombardmentReduction` entry (Bunker / Shield mitigation).
 *   8. The first effect, fall-through, in array order.
 *
 * The rule favours economic flow over storage caps so e.g. a structure
 * that does both ("+0.5 credits / tick AND +10,000 credits cap")
 * surfaces the per-tick contribution — usually the more interesting
 * read for a build decision.
 */
function pickHeadlineEffect(
  effects: readonly StructureEffect[],
): StructureEffect | null {
  if (effects.length === 0) return null;
  // Priority-ordered scan; returns first match per category.
  const find = (predicate: (e: StructureEffect) => boolean): StructureEffect | undefined =>
    effects.find(predicate);
  return (
    find((e) => e.kind === 'perTickIncome') ??
    find((e) => e.kind === 'multiplier') ??
    find((e) => e.kind === 'capBonus') ??
    find((e) => e.kind === 'shipyardClassGate') ??
    find((e) => e.kind === 'sensorRangeGalaxy') ??
    find((e) => e.kind === 'weaponEmitter') ??
    find((e) => e.kind === 'bombardmentReduction') ??
    effects[0]!
  );
}

/**
 * One-line summary of a single effect at a given level. The
 * `withLevelSuffix` flag controls whether the string is suffixed with
 * `" at L<n>"` — `true` for the build-menu second line (no level
 * context elsewhere), `false` for the structure row third line + the
 * effect ladder (level context lives on the line above / left).
 */
function formatEffectAtLevel(
  effect: StructureEffect,
  level: number,
  withLevelSuffix: boolean,
): string {
  const tail = withLevelSuffix ? ` at L${level}` : '';
  switch (effect.kind) {
    case 'multiplier': {
      const v = effect.perLevel[level - 1] ?? 1;
      // Multiplier values are absolute (1.10 = +10%, not 0.10). Convert
      // to a delta percent for the player-facing string — 1.00 reads as
      // "no change", which we render as "0%" for completeness.
      const deltaPct = Math.round((v - 1) * 100);
      const sign = deltaPct >= 0 ? '+' : '';
      return `${sign}${deltaPct}% ${labelForResource(effect.resource)}${tail}`;
    }
    case 'perTickIncome': {
      const v = effect.perLevel[level - 1] ?? 0;
      // Two-decimal precision is enough — typical authored values are
      // 0.05 / 0.5 / 2 / 10. Integers print without the trailing .00
      // for a less-noisy build menu.
      const formatted = Number.isInteger(v) ? String(v) : v.toFixed(2);
      const sign = v >= 0 ? '+' : '';
      return `${sign}${formatted} ${labelForResource(effect.resource)} / tick${tail}`;
    }
    case 'capBonus': {
      const v = effect.perLevel[level - 1] ?? 0;
      // Commas for readability on the 10,000 / 50,000 cap values that
      // dominate authored capBonus rows.
      const formatted = v.toLocaleString('en-US');
      const sign = v >= 0 ? '+' : '';
      return `${sign}${formatted} ${labelForResource(effect.resource)} cap${tail}`;
    }
    case 'bombardmentReduction': {
      const v = effect.perLevel[level - 1] ?? 0;
      // Values authored as multipliers (0.20 = 20% damage reduction).
      const pct = Math.round(v * 100);
      return `-${pct}% bombardment damage${tail}`;
    }
    case 'sensorRangeGalaxy': {
      const r = effect.rangeByLevel[level - 1] ?? 0;
      return `Covers systems within ${r.toFixed(2)} galaxy radii${tail}`;
    }
    case 'shipyardClassGate': {
      const cls = effect.perLevel[level - 1] ?? 0;
      return `Unlocks Class ${cls} ship builds${tail}`;
    }
    case 'weaponEmitter': {
      const slots = effect.slotCountPerLevel[level - 1] ?? 0;
      return `+${slots} ${effect.category} weapon slot${slots === 1 ? '' : 's'}${tail}`;
    }
  }
}

/**
 * Resource label normalisation. `'popGrowth'` reads as "pop" rather
 * than the camelCase enum value; `'diplomatic'` reads as "diplomatic
 * influence" since the bare word is ambiguous. All other resource
 * names pass through verbatim — they're already player-facing.
 */
function labelForResource(resource: string): string {
  switch (resource) {
    case 'popGrowth':
      return 'pop';
    case 'diplomatic':
      return 'diplomatic influence';
    default:
      return resource;
  }
}

// ---------------------------------------------------------------------------
// Re-export the StructureRow type used by external consumers of the
// helpers so `import type { StructureRow } from '../ui/structure-summaries'`
// works without round-tripping through state/types. Bundlers tree-shake
// type-only re-exports.
// ---------------------------------------------------------------------------

export type { StructureRow };
